import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { noop, uniqBy } from 'lodash';

import { FETCHING_STATUSES } from 'constants/globals';
import abortable, { AbortablePromise, tryAbortPromise } from 'lib/abortablePromise';
import * as apiV6 from 'lib/apiV6';
import { getErrorHandler } from 'slices/app';

const SLICE_NAME = 'telegramDialogs';

let fetchDialogs: null | AbortablePromise<{
  payload: { count: number; next: string; previous: string; results: AH$TelegramDialog[] };
}> = null;

let fetchMessages: null | AbortablePromise<{
  payload: {
    count: number;
    next: string | null;
    previous: string | null;
    results: AH$TelegramDialogMessage[];
  };
}> = null;

let fetchMarkAsReadMessage: null | AbortablePromise<void> = null;

export type TelegramDialogsState = {
  banErrorText: string | null;
  dialogs: {
    count: number;
    fetchingStatus: $Values<typeof FETCHING_STATUSES>;
    next: string | null;
    offset: number;
    previous: string | null;
    results: AH$TelegramDialog[];
    sortByRecruiter: string;
    sortBySimCard: string;
  };
  messages: {
    count: number;
    fetchingStatus: $Values<typeof FETCHING_STATUSES>;
    fetchingStatusLoadMore: $Values<typeof FETCHING_STATUSES>;
    maxMessageId: null | number;
    next: string | null;
    offset: number;
    previous: string | null;
    results: AH$TelegramDialogMessage[];
  };
  newCreatedDialog: {
    fetchingStatus: $Values<typeof FETCHING_STATUSES>;
    result: AH$TelegramDialog | null;
  };
  sendMessageFetchingStatus: $Values<typeof FETCHING_STATUSES>;
};

export const telegramDialogsInitialState: TelegramDialogsState = {
  newCreatedDialog: {
    fetchingStatus: 'SUCCESS',
    result: null,
  },
  dialogs: {
    count: 0,
    next: '',
    previous: '',
    results: [],
    offset: 0,
    fetchingStatus: 'SUCCESS',
    sortByRecruiter: '',
    sortBySimCard: '',
  },
  messages: {
    results: [],
    count: 0,
    offset: 0,
    next: null,
    previous: null,
    maxMessageId: null,
    fetchingStatus: 'SUCCESS',
    fetchingStatusLoadMore: 'SUCCESS',
  },
  sendMessageFetchingStatus: 'SUCCESS',
  banErrorText: null,
};

export const getAllDialogs = createAsyncThunk(
  `${SLICE_NAME}/getAllDialogs`,
  async (
    {
      search,
      created_by_id,
      limit = 20,
      offset = 0,
      only_unread,
      telegram_account_id,
      only_need_attention,
      onSuccess = noop,
      onError = noop,
    }: {
      created_by_id?: number;
      limit?: number;
      offset?: number;
      onError?: (error: string) => void;
      onSuccess?: () => void;
      only_need_attention?: string;
      only_unread?: string;
      search?: string;
      telegram_account_id?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      if (fetchDialogs && fetchDialogs.abort) {
        fetchDialogs.abort();
      }

      fetchDialogs = abortable(
        apiV6.getTelegramDialogs({
          created_by_id,
          limit,
          offset,
          search,
          only_unread,
          only_need_attention,
          telegram_account_id,
        })
      );

      const { payload } = await fetchDialogs;

      onSuccess();

      return payload;
    } catch (e) {
      if (e !== 'ABORTED_PROMISE') {
        getErrorHandler({
          parseError: true,
          silent: true,
          getError: ({ error }: { error: string }) => {
            onError(error);
          },
        })(e);
        return rejectWithValue(e);
      }

      return undefined;
    }
  }
);

export const getTelegramDialogById = createAsyncThunk(
  `${SLICE_NAME}/getTelegramDialogById`,
  async (
    {
      dialog_id,
      onSuccess = noop,
    }: {
      dialog_id: string;
      onSuccess?: (results: AH$TelegramDialog) => void;
    },
    { rejectWithValue }
  ) => {
    try {
      const { payload } = await apiV6.getTelegramDialogId(dialog_id);

      onSuccess(payload);
      return payload;
    } catch (e) {
      getErrorHandler()(e);

      return rejectWithValue(e);
    }
  }
);

export const downloadTelegramAttachmentThunk = createAsyncThunk(
  `${SLICE_NAME}/downloadTelegramAttachment`,
  async (
    {
      dialog_id,
      message_id,
      onLoading = noop,
      onError = noop,
    }: {
      dialog_id: number;
      message_id: number;
      onError?: (error: string) => void;
      onLoading?: (loading: boolean) => void;
    },
    { rejectWithValue }
  ) => {
    try {
      onLoading(true);
      const data = await apiV6.downloadTelegramAttachment(dialog_id, message_id);

      return data;
    } catch (e) {
      getErrorHandler({
        parseError: true,
        silent: true,
        getError: ({ error }: { error: string }) => {
          onError?.(error);
        },
      })(e);

      return rejectWithValue(e);
    } finally {
      onLoading(false);
    }
  }
);

export const getTelegramDialogIdMessages = createAsyncThunk(
  `${SLICE_NAME}/getTelegramDialogIdMessages`,
  async (
    {
      id,
      limit = 5,
      offset = 0,
      isFirstRequest = false,
      onSuccess = noop,
    }: {
      id: string;
      isFirstRequest: boolean;
      limit?: number;
      offset?: number;
      onSuccess?: (results: AH$TelegramDialogMessage[], count: number) => void;
    },
    { rejectWithValue }
  ) => {
    try {
      if (fetchMessages && fetchMessages.abort) {
        fetchMessages.abort();
      }
      fetchMessages = abortable(apiV6.getTelegramDialogIdMessages({ id, limit, offset }));
      const { payload } = await fetchMessages;

      onSuccess(payload.results, payload.count);

      return { ...payload, results: payload.results.reverse(), isFirstRequest };
    } catch (e) {
      if (e !== 'ABORTED_PROMISE') {
        getErrorHandler()(e);
        return rejectWithValue(e);
      }

      return undefined;
    }
  }
);

export const createTelegramDialog = createAsyncThunk(
  `${SLICE_NAME}/createTelegramDialog`,
  async (
    {
      profile_id,
      username,
      onSuccess = noop,
      onError = noop,
    }: {
      username: string;
      onError?: (error: string) => void;
      onSuccess?: (dialogId: number) => void;
      profile_id?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const { payload } = await apiV6.createTelegramDialog({ profile_id, username });

      onSuccess(payload.id);

      return payload;
    } catch (e) {
      getErrorHandler({
        parseError: true,
        silent: true,
        getError: (error: Record<string, any>) => {
          try {
            const parsedError = JSON.parse(error.error);

            if (parsedError.error === 'TelegramUnknownError') {
              onError(parsedError.message);
            } else {
              onError(parsedError.error);
            }
          } catch (e) {
            if ('error' in error) {
              onError(error.error);
            } else {
              const firstKey = Object.keys(error)[0];
              const firstError = error[firstKey][0];

              onError(firstError);
            }
          }
        },
      })(e);

      return rejectWithValue(e);
    }
  }
);

export const sendTelegramMessage = createAsyncThunk(
  `${SLICE_NAME}/sendTelegramMessage`,
  async (
    {
      dialogId,
      message,
      onSuccess = noop,
      onError = noop,
    }: {
      dialogId: string;
      message: string;
      onError: (error: string) => void;
      onSuccess?: (data: AH$TelegramDialogMessage) => void;
    },
    { rejectWithValue }
  ) => {
    try {
      const { payload } = await apiV6.sendTelegramMessage({ dialogId, message });

      onSuccess(payload);

      return payload;
    } catch (e) {
      getErrorHandler({
        parseError: true,
        silent: true,
        getError: (error: Record<string, any>) => {
          try {
            const parsedError = JSON.parse(error.error);

            if (parsedError.error === 'TelegramUnknownError') {
              onError(parsedError.message);
            } else {
              onError(parsedError.error);
            }
          } catch (e) {
            if ('error' in error) {
              onError(error.error);
            } else {
              const firstKey = Object.keys(error)[0];
              const firstError = error[firstKey][0];

              onError(firstError);
            }
          }
        },
      })(e);

      return rejectWithValue(e);
    }
  }
);

export const markAsReadMessage = createAsyncThunk(
  `${SLICE_NAME}/markAsReadMessage`,
  async ({
    messageId,
    dialogId,
    onSuccess = noop,
    onError = noop,
  }: {
    dialogId: number;
    messageId: number;
    onError: (error: string) => void;
    onSuccess?: () => void;
  }) => {
    if (fetchMarkAsReadMessage && fetchMarkAsReadMessage.abort) {
      fetchMarkAsReadMessage.abort();
    }
    try {
      fetchMarkAsReadMessage = abortable(apiV6.telegramMarkAsReadMessage({ messageId, dialogId }));
      await fetchMarkAsReadMessage;

      onSuccess();
    } catch (e) {
      getErrorHandler({
        parseError: true,
        silent: true,
        getError: (error: Record<string, any>) => {
          try {
            const parsedError = JSON.parse(error.error);

            if (parsedError.error === 'TelegramUnknownError') {
              onError(parsedError.message);
            } else {
              onError(parsedError.error);
            }
          } catch (e) {
            if ('error' in error) {
              onError(error.error);
            } else {
              const firstKey = Object.keys(error)[0];
              const firstError = error[firstKey][0];

              onError(firstError);
            }
          }
        },
      })(e);
    }
  }
);

const telegramDialogsSlice = createSlice({
  name: SLICE_NAME,
  initialState: telegramDialogsInitialState,
  reducers: {
    resetDialogs: (state) => {
      state.dialogs = telegramDialogsInitialState.dialogs;
      tryAbortPromise(fetchDialogs);
    },
    setDialogUnreadCount: (state, action: PayloadAction<number>) => {
      const dialogId = action.payload;
      const dialogIndex = state.dialogs.results.findIndex((el) => el.id === dialogId);

      if (dialogIndex !== -1) {
        state.dialogs.results[dialogIndex].unread_count = 0;
      }
    },
    setOffsetDialogs: (state, action: PayloadAction<number>) => {
      state.dialogs.offset = action.payload;
    },
    setOffsetMessages: (state, action: PayloadAction<number>) => {
      state.messages.offset = action.payload;
    },
    setNewDialog: (state, action: PayloadAction<AH$TelegramDialog>) => {
      state.dialogs.results.unshift(action.payload);
      state.dialogs.count += 1;
    },
    resetNewCreatedDialog: (state) => {
      state.newCreatedDialog = { fetchingStatus: 'SUCCESS', result: null };
    },
    setMaxReadMessageId: (state, action: PayloadAction<number | null>) => {
      state.messages.maxMessageId = action.payload;
    },
    setUpdateDialog: (state, action: PayloadAction<{ updateDialog: AH$TelegramDialog }>) => {
      const dialogIndex = state.dialogs.results.findIndex(
        (dialog) => dialog.id === action.payload.updateDialog.id
      );

      if (dialogIndex !== -1) {
        state.dialogs.results[dialogIndex] = action.payload.updateDialog;
      }
    },
    setLastMessageForDialogId: (
      state,
      action: PayloadAction<{ dialogId: number; message: AH$TelegramDialogMessage }>
    ) => {
      const dialogIndex = state.dialogs.results.findIndex(
        (dialog) => dialog.id === action.payload.dialogId
      );
      const dialog = state.dialogs.results[dialogIndex];

      state.dialogs.results[dialogIndex] = { ...dialog, last_message: action.payload.message };

      if (!state.dialogs.results[dialogIndex].last_read_message_id) {
        state.dialogs.results[dialogIndex].last_read_message_id = action.payload.message.message_id;
      }
    },
    setBanErrorText: (state, action: PayloadAction<string>) => {
      state.banErrorText = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllDialogs.pending, (state) => {
      state.dialogs.fetchingStatus = FETCHING_STATUSES.LOADING;
    });
    builder.addCase(getAllDialogs.fulfilled, (state, action) => {
      if (action.payload) {
        state.dialogs.fetchingStatus = FETCHING_STATUSES.SUCCESS;
        state.dialogs.next = action.payload.next;
        state.dialogs.previous = action.payload.previous;
        state.dialogs.count = action.payload?.count;
        state.dialogs.results = uniqBy([...state.dialogs.results, ...action.payload.results], 'id');
      }
    });
    builder.addCase(getAllDialogs.rejected, (state) => {
      state.dialogs.fetchingStatus = FETCHING_STATUSES.ERROR;
    });
    builder.addCase(getTelegramDialogIdMessages.pending, (state, action) => {
      state.messages.fetchingStatus = action.meta.arg.isFirstRequest
        ? FETCHING_STATUSES.LOADING
        : FETCHING_STATUSES.SUCCESS;
      state.messages.fetchingStatusLoadMore = action.meta.arg.isFirstRequest
        ? FETCHING_STATUSES.SUCCESS
        : FETCHING_STATUSES.LOADING;
    });
    builder.addCase(getTelegramDialogIdMessages.fulfilled, (state, action) => {
      if (action.payload) {
        state.messages.fetchingStatus = FETCHING_STATUSES.SUCCESS;
        state.messages.fetchingStatusLoadMore = FETCHING_STATUSES.SUCCESS;
        state.messages.results = action.payload.isFirstRequest
          ? action.payload.results
          : [...action.payload.results, ...state.messages.results];
        state.messages.next = action.payload.next;
        state.messages.previous = action.payload.previous;
        state.messages.count = action.payload.count;
      }
    });
    builder.addCase(getTelegramDialogIdMessages.rejected, (state) => {
      state.messages.fetchingStatus = FETCHING_STATUSES.ERROR;
    });
    builder.addCase(createTelegramDialog.pending, (state) => {
      state.newCreatedDialog.fetchingStatus = FETCHING_STATUSES.LOADING;
    });
    builder.addCase(createTelegramDialog.fulfilled, (state, action) => {
      state.newCreatedDialog.fetchingStatus = FETCHING_STATUSES.SUCCESS;
      state.newCreatedDialog.result = action.payload;
    });
    builder.addCase(createTelegramDialog.rejected, (state) => {
      state.newCreatedDialog.fetchingStatus = FETCHING_STATUSES.ERROR;
    });
    builder.addCase(sendTelegramMessage.pending, (state) => {
      state.sendMessageFetchingStatus = FETCHING_STATUSES.LOADING;
    });
    builder.addCase(sendTelegramMessage.fulfilled, (state, action) => {
      state.sendMessageFetchingStatus = FETCHING_STATUSES.SUCCESS;
      state.messages.results = [...state.messages.results, action.payload];
    });
    builder.addCase(sendTelegramMessage.rejected, (state) => {
      state.sendMessageFetchingStatus = FETCHING_STATUSES.ERROR;
    });
  },
});

export const {
  resetDialogs,
  setOffsetDialogs,
  setOffsetMessages,
  setMaxReadMessageId,
  setLastMessageForDialogId,
  setBanErrorText,
  setNewDialog,
  setDialogUnreadCount,
  setUpdateDialog,
} = telegramDialogsSlice.actions;
export const telegramDialogs = telegramDialogsSlice.reducer;
